import { useState, BaseSyntheticEvent, ChangeEvent, KeyboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import styles from "./LoginForm.module.css";
import { path, validatePassword, validateFullEmail } from "../common/Common";
import { AccountManager } from "../../workers/AccountManager";
import { ELoginType } from "../../workers/ELoginType";

import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import TextInput from "../common/TextInput";
import { emitAuthEvent } from "../../module/auth";

// 💠 25.03.20.THU | YoungJun | LoginForm 작성

interface LoginFormProps {
    onClickSignUp: (event: BaseSyntheticEvent) => void;
    onClose: () => void;
    goRefresh?: boolean; // 로그인 완료 후 현재 페이지 새로고침 여부 prop 추가 | smyun | 250709
}

const LoginForm: React.FC<LoginFormProps> = ({ onClose, onClickSignUp, goRefresh }) => {
    const { ModalComponent, openModal, closeModal } = useModal();
    const navigate = useNavigate();

    const [logInUserInfo, setLogInUserInfo] = useState({
        userId: "",
        password: "",
        saveid: false,
        autologin: false,
    });

    const [isValidId, setIsValidId] = useState(true); // isValid = id 유효 여부 (true면 정상, false면 경고 노출)
    const [isValidPassword, setIsValidPassword] = useState(true); // 형식 검증 비사용 시 true로 둠

    /**
     * 함수 ------------------------------------------------------------------------------------------------
     */

    // 250909 | smyun | 반복되는 모달 부분 함수로 분리
    const showModal = (message: React.ReactNode) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    // 250909 | 검증로직 수정 및 강화 하여 분리
    // 입력 즉시 검증
    const handleFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = event.target;
        const next = typeof value === "string" ? value : String(value);

        setLogInUserInfo((prev) => ({ ...prev, [name]: next }));

        if (name === "userId") {
            const trimmed = next.trim();
            if (trimmed === "") {
                // 아이디를 다 지우면 메시지도 사라짐
                setIsValidId(true);
            } else {
                // 이메일 형식 검증
                // 250610 | smyun | 이메일, 비밀번호 형식 유효성 검사 추가
                setIsValidId(validateFullEmail(trimmed));
            }
        }

        if (name === "password") {
            // 비밀번호 유효성 검사
            setIsValidPassword(true);
            /* 비밀번호 검증 조건은 특수문자가 불가능한데, 관리자 비밀번호에 특수문자 있어서 검증 함수 주석처리 */
            // if (logInUserInfo.password !== "") {
            //     setIsValidPassword(validatePassword(logInUserInfo.password));
            // }
        }
    };

    // 아이디저장 & 자동로그인 숨김 처리 중
    // const checkBoxClicked = (e: ChangeEvent<HTMLInputElement>) => {
    //     var key = e.target.name;
    //     var checked = e.target.checked;

    //     setLogInUserInfo({
    //         ...logInUserInfo,
    //         [key]: checked,
    //     });
    // };

    // 🔒 로그인 버튼 활성화 여부: 이메일/비번 채워지고, 이메일 형식 OK 일 때 | 250909 | smyun
    const canSubmit = logInUserInfo.userId.trim() !== "" && isValidId && logInUserInfo.password.trim() !== "" && isValidPassword;

    const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if (e.key === "Enter") {
            canSubmit && logInBtnClicked(e as any); // 250909 | smyun | 엔터도 검증 로직 태움
        }
    };

    // 250909 | smyun | 로그인버튼 클릭 시 검증 (검증 분리)
    const logInBtnClicked = (e: BaseSyntheticEvent) => {
        const id = logInUserInfo.userId.trim();
        const pw = logInUserInfo.password.trim();

        // 1) 아이디 빈 값 검증
        if (id == "") {
            showModal("아이디를 입력해주세요.");
            return;
        }

        // 2) 비밀번호 빈값
        if (pw == "") {
            showModal("비밀번호를 입력해주세요.");
            return;
        }

        // 3) 통과 시 로그인
        logInUser();
    };

    const logInUser = async () => {
        const accountManager = AccountManager.getInstance();
        const logInResult = await accountManager.generalLogin(logInUserInfo);

        const code = logInResult.code;
        const flag = logInResult.flag;
        const desc = logInResult.desc.split("] ")[1]; // 성공일 때는 undefined

        if (flag) {
            // 로그인 방식 완전변경 | 250905 | smyun
            emitAuthEvent("login"); // 탭 싱크 이벤트

            // 일부 동기화가 덜 된 컴포넌트를 위해 새로고칩 옵션 남겨둠
            goRefresh && navigate(0);

            onClose();
        } else {
            // 250909| smyun 서버에서 500에러 대신 해당 상황에 맞는 HttpStatus를 보내도록 수정하여 상황에 맞게 UI 표출
            // 자격 증명 불일치 팝업
            if (code == "WRONG ID" || "WRONG PASSWORD") {
                showModal(logInResult.desc); // "아이디 또는 비밀번호가 일치하지 않습니다." 대신 상황에 맞는 설명 표출
            } else {
                // 기타 서버 오류에 대한 일반 안내
                showModal(logInResult.desc || "로그인 처리 중 오류가 발생했습니다.");
            }
        }
    };

    const portalLogInUser = async (type: ELoginType) => {
        const accountManager = AccountManager.getInstance();
        const result = await accountManager.login(type);

        // 소셜로그인 시에도 자동 감지(localStorage 이벤트 추가부 삭제) | smyun | 250906
        if (result && result.flag) {
            onClose();
        }
    };

    return (
        <>
            <ModalComponent />
            <div className={styles.login_container}>
                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.login_input_title}>
                        <p>아이디</p>
                    </div>
                    <div className={styles.row} style={{ textAlign: "left" }}>
                        <TextInput
                            name="userId"
                            placeholder="이메일"
                            value={logInUserInfo.userId}
                            onChange={handleFormChange}
                            onKeyDown={onKeyDown}
                            isValid={isValidId}
                            validMsg={"이메일 형식을 확인해주세요."} // 값이 있으면서 형식이 틀릴 때만 노출됨
                        />
                    </div>
                </div>

                <div className={`${styles.container} ${styles.row}`}>
                    <div className={styles.login_input_title}>
                        <p>비밀번호</p>
                    </div>
                    <div className={`${styles.row} ${styles.feedback_container}`} style={{ textAlign: "left" }}>
                        <TextInput
                            name="password"
                            type="password"
                            placeholder="비밀번호"
                            value={logInUserInfo.password}
                            onChange={handleFormChange}
                            onKeyDown={onKeyDown}
                            isValid={isValidPassword}
                            validMsg={"비밀번호는 영문, 숫자를 포함한 8~16자리 이내만 가능합니다."}
                        />
                    </div>
                </div>

                {/* 아이디저장 & 자동로그인 숨김 처리 */}
                {/* <div className={styles.login_options} style={{ display: "none" }}>
                    <label>
                        <input type="checkbox" checked={logInUserInfo.saveid} onChange={checkBoxClicked} />
                        아이디 저장
                    </label>
                    <label>
                        <input type="checkbox" checked={logInUserInfo.autologin} onChange={checkBoxClicked} />
                        자동로그인
                    </label>
                </div> */}

                {/* 250909 | smyun | 🔘 로그인버튼 비활성화: 이메일 형식 OK + 비밀번호 입력 시에만 활성화 - disabled */}
                <button className={styles.login_button} onClick={logInBtnClicked} disabled={!canSubmit} aria-disabled={!canSubmit}>
                    로그인
                </button>

                <div className={styles.login_options} style={{ justifyContent: "flex-end" }}>
                    <span className={styles.login_options_link} onClick={onClickSignUp}>
                        회원가입
                    </span>
                    <Link className={styles.login_options_link} to={path.FIND_ACCOUNT} onClick={onClose}>
                        아이디/비밀번호 찾기
                    </Link>
                </div>

                {/* 소셜 로그인 버튼 */}
                <div className={styles.social_button_container}>
                    <button className={`${styles.social_button} ${styles.google}`} onClick={() => portalLogInUser(ELoginType.GOOGLE)}>
                        구글 아이디로 로그인
                    </button>
                    <button className={`${styles.social_button} ${styles.naver}`} onClick={() => portalLogInUser(ELoginType.NAVER)}>
                        네이버 아이디로 로그인
                    </button>
                    <button className={`${styles.social_button} ${styles.kakao}`} onClick={() => portalLogInUser(ELoginType.KAKAO)}>
                        카카오 아이디로 로그인
                    </button>
                    <button className={`${styles.social_button} ${styles.facebook}`} onClick={() => portalLogInUser(ELoginType.FACEBOOK)}>
                        페이스북 아이디로 로그인
                    </button>
                </div>
            </div>
        </>
    );
};

export default LoginForm;
